import { reactive, toRefs, computed, watch, toRaw } from 'vue'
import { slugify, uuid, log } from './helpers'
import { useWidget } from './widget'
import { watchDeep } from '@vueuse/core'

class Message {
  constructor({
    sender,
    content,
    id,
    suggestedQuestions = [],
    bot = false,
    status = 'pending',
    source = [],
    confidenceScore = null
  }) {
    this.time = new Date().toISOString()
    this.sender = sender
    this.id = id
    this.content = content
    this.status = status
    this.bot = bot
    this.source = []
    this.confidenceScore = confidenceScore
    if (source.length > 0) {
      this.addSources(source)
    }

    this.suggestedQuestions = suggestedQuestions
  }

  setStatus(status) {
    this.status = status
  }
  setContent(content) {
    this.content = content
  }
  editContent(newContent, status = 'pending') {
    this.content = newContent
    this.status = status
  }
  setConfidenceScore(score) {
    this.confidenceScore = score
  }
  addSuggestedQuestion(question) {
    this.suggestedQuestions.push(question)
  }
  setSuggestedQuestion(questions) {
    this.suggestedQuestions = questions
  }
  addSources = (sources) => {
    if (!sources || sources.length === 0) return
    this.source = []
    sources.forEach((source) => {
      this.addSource(source)
    })
  }
  addSource(source) {
    const s = typeof source === 'string' ? JSON.parse(source) : source
    try {
      if (
        s.context === 'product' ||
        s.source_type === 'product' ||
        s.document_context === 'product'
      ) {
        if (s.source_type === 'product') {
          s.title = s.attributes?.name?.pl || s.attributes?.name || `null`
        } else if (s.document_context === 'product' || s.context === 'product') {
          s.title = s.attributes?.name || `null`
        } else {
          s.title = `null`
        }
        this.source.push(s)
        return
      }
      if (s.title) {
        s.id = slugify(s.title)
        if (this.source.find((el) => el.id === s.id)) return
        this.source.push(s)
      }
    } catch (e) {
      log(e)
    }
  }
  pushSuggestions(suggestions) {
    this.suggestedQuestions.push(suggestions)
  }

  setError() {
    this.content =
      'Przepraszam, ale .... coś nie tak z bazą wiedzy.... spróbuj jeszcze raz. Ewentualnie skontaktuj się z administratorem w celu wyjaśnienia problemu.'
  }
}

const stateMessages = reactive({
  messages: [],
  currentBotMessageId: null,
  isHistoryLoading: false,
  currentMessage: computed(() => {
    return (
      stateMessages.messages.find((message) => message.id === stateMessages.currentBotMessageId) ||
      null
    )
  })
})

export const useMessages = () => {
  const { options, config, widgetLocalStorage } = useWidget()
  const initSuggestedQuestion = computed(() => {
    return options.suggestedQuestion
  })

  const getRagHistory = async () => {
    try {
      const base = config.chatUrl.endsWith('/') ? config.chatUrl : `${config.chatUrl}/`

      stateMessages.isHistoryLoading = true
      const link = `${base}assistants/${config.assistantId}/chat-history/${options.chat_id}`

      // const link = `https://rag-engine.x-one.dev/assistants/efa94790-0f80-404f-be14-6e164562abd8/chat-history/067e3ba5-e6d7-74e5-8000-51a98ed6e84e`
      const response = await fetch(link, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.accessToken}`
        }
      })
      if (response.ok) {
        const { data } = await response.json()
        log(data)
        options.chat_id = data.id
        if (data && data.messages.length) {
          data.messages.forEach((msg, idx) => {
            const id = uuid()
            const objToMessage = {
              sender: msg.role === 'user' ? options.userName : options.assistantName,
              content: msg.message,
              id,
              suggestedQuestions: msg.suggested_questions,
              bot: msg.role === 'assistant',
              status: 'sent',
              source: idx > 0 ? data.messages[idx - 1].contexts : [],
              confidenceScore: null
            }

            stateMessages.messages.push(new Message(objToMessage))
            stateMessages.currentBotMessageId = id
          })
        }
      } else {
        console.error('Error fetching history:', response.statusText)
      }
    } catch (e) {
      console.error('Error fetching history:', e)
    } finally {
      stateMessages.isHistoryLoading = false
    }
  }
  const setHistory = (history) => {
    if (!history || history.length === 0) return
    stateMessages.messages = []
    history.forEach((msg) => {
      const message = new Message(msg)
      message.setStatus('sent')
      message.addSources(msg.source)
      stateMessages.messages.push(new Message(msg))
    })
  }
  const setInitSuggestionQuestions = (suggestedString) => {
    stateMessages.messages[0].setSuggestedQuestion(suggestedString)
  }
  const pushMessage = (message) => {
    stateMessages.messages.push(new Message(message))
  }

  const editMessage = (id, newContent, status) => {
    const message = stateMessages.messages.find((msg) => msg.id === id)
    if (message) {
      message.editContent(newContent, status)
    }
  }

  const deleteMessage = (id) => {
    stateMessages.messages = stateMessages.messages.filter((msg) => msg.id !== id)
  }
  const resetMessages = () => {
    stateMessages.messages = []
    stateMessages.currentBotMessageId = null
  }
  const initMessage = async () => {
    if (options.chat_id) {
      await getRagHistory()
      return
    }

    if (stateMessages.messages.length !== 0) return
    const id = uuid()
    pushMessage({
      id,
      sender: options.assistantName,
      content: options.initialMessage,
      status: 'sent',
      bot: true,
      source: [],
      suggestedQuestions: initSuggestedQuestion.value
    })
    stateMessages.currentBotMessageId = id
  }

  return {
    ...toRefs(stateMessages),
    pushMessage,
    editMessage,
    deleteMessage,
    setHistory,
    setInitSuggestionQuestions,
    resetMessages,
    initMessage,
    initSuggestedQuestion,
    getRagHistory
  }
}
